import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useProfileMutations } from '@/hooks/profile';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission } from '@/hooks/permissions/useHasPermission';
import { AvatarUpload } from './AvatarUpload';
import type { Profile, ProfileFormData } from '@/services/profile/types';

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile?: Profile; // If provided, editing another user's profile
}

export const EditProfileDialog = ({ open, onOpenChange, profile: targetProfile }: EditProfileDialogProps) => {
  // ALL HOOKS MUST BE AT THE TOP - before any conditional returns
  const { profile: currentUserProfile } = useAuth();
  const { updateMyProfile, updateUserProfile, uploadAvatar, deleteAvatar } = useProfileMutations();
  
  // Use permission system for editing other profiles
  const canEditOtherProfiles = useHasPermission('manage_employee_details');
  
  // State hooks - must be before any early returns
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarError, setAvatarError] = useState<string>('');
  const [formData, setFormData] = useState<ProfileFormData>({
    preferredName: '',
    firstName: '',
    lastName: '',
    address: '',
    email: '',
    mobilePhone: '',
    dateOfBirth: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    weeklyHours: null,
    fte: 1.0,
  });

  // Determine if editing own profile or another user's profile
  const isEditingOwnProfile = !targetProfile;
  const profileToEdit = targetProfile || currentUserProfile;

  // Initialize form data when profile changes
  useEffect(() => {
    if (profileToEdit) {
      setFormData({
        preferredName: profileToEdit.preferred_name || '',
        firstName: profileToEdit.first_name || '',
        lastName: profileToEdit.last_name || '',
        address: profileToEdit.address || '',
        email: profileToEdit.email || '',
        mobilePhone: profileToEdit.mobile_phone || '',
        dateOfBirth: profileToEdit.date_of_birth || '',
        emergencyContactName: profileToEdit.emergency_contact_name || '',
        emergencyContactPhone: profileToEdit.emergency_contact_phone || '',
        weeklyHours: (profileToEdit as any).weekly_hours ?? null,
        fte: (profileToEdit as any).fte ?? 1.0,
      });
    }
  }, [profileToEdit]);

  // EARLY RETURN - After all hooks are called
  // Prevent unauthorized access
  if (!isEditingOwnProfile && !canEditOtherProfiles) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const updateInput = {
        preferredName: formData.preferredName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        email: formData.email,
        mobilePhone: formData.mobilePhone,
        dateOfBirth: formData.dateOfBirth,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        weeklyHours: formData.weeklyHours,
        fte: formData.fte,
      };

      if (isEditingOwnProfile) {
        await updateMyProfile.mutateAsync(updateInput);
      } else if (targetProfile) {
        await updateUserProfile.mutateAsync({
          userId: targetProfile.id,
          input: updateInput,
        });
      }

      onOpenChange(false);
    } catch (error) {
      // Error handling - mutation will show toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAvatarUpload = async (file: File) => {
    if (!profileToEdit || !currentUserProfile?.company_id) return;
    
    setAvatarError('');
    try {
      await uploadAvatar.mutateAsync({
        file,
        userId: profileToEdit.user_id, // For storage RLS compliance
        companyId: currentUserProfile.company_id,
        profileId: isEditingOwnProfile ? undefined : profileToEdit.id, // For profile update
      });
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to upload avatar');
    }
  };

  const handleAvatarRemove = async () => {
    if (!profileToEdit?.avatar_url) return;
    
    setAvatarError('');
    try {
      await deleteAvatar.mutateAsync(profileToEdit.avatar_url);
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Failed to remove avatar');
    }
  };

  const getUserInitials = (profile: { first_name?: string | null; last_name?: string | null; preferred_name?: string | null } | null | undefined) => {
    if (!profile) return 'U';
    const firstName = profile.first_name || profile.preferred_name || '';
    const lastName = profile.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditingOwnProfile ? 'Edit Profile' : `Edit ${profileToEdit?.first_name}'s Profile`}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Personal Information</h3>
            
            {/* Profile Picture Upload */}
            <div className="space-y-2">
              <Label>Profile Picture</Label>
              <AvatarUpload
                currentAvatarUrl={profileToEdit?.avatar_url}
                userInitials={getUserInitials(profileToEdit)}
                onFileSelect={handleAvatarUpload}
                onRemove={handleAvatarRemove}
                isUploading={uploadAvatar.isPending}
                error={avatarError}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="preferredName">Preferred Name</Label>
                <Input
                  id="preferredName"
                  value={formData.preferredName}
                  onChange={(e) => handleInputChange('preferredName', e.target.value)}
                  placeholder="How you'd like to be called"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name (Legal)</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name (Legal)</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mobilePhone">Mobile Phone Number</Label>
                <Input
                  id="mobilePhone"
                  type="tel"
                  value={formData.mobilePhone}
                  onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Street address, city, state, postal code"
                rows={3}
              />
            </div>
          </div>

          {/* Contract Details Section - Phase 5 */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Contract Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="weeklyHours">Contract Weekly Hours</Label>
                <Input
                  id="weeklyHours"
                  type="number"
                  step="0.5"
                  min="0"
                  max="168"
                  value={formData.weeklyHours ?? ''}
                  onChange={(e) => handleInputChange('weeklyHours', e.target.value ? parseFloat(e.target.value) : null as any)}
                  placeholder="40"
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use company default (40 hrs/week). Used for salary calculations.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fte">Full-Time Equivalent (FTE)</Label>
                <Select 
                  value={formData.fte?.toString() || '1.0'} 
                  onValueChange={(val) => handleInputChange('fte', parseFloat(val) as any)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.0">1.0 (100% - Full Time)</SelectItem>
                    <SelectItem value="0.9">0.9 (90%)</SelectItem>
                    <SelectItem value="0.8">0.8 (80%)</SelectItem>
                    <SelectItem value="0.6">0.6 (60%)</SelectItem>
                    <SelectItem value="0.5">0.5 (50% - Half Time)</SelectItem>
                    <SelectItem value="0.4">0.4 (40%)</SelectItem>
                    <SelectItem value="0.2">0.2 (20%)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Applies to salary calculations (e.g., 0.5 = part-time at 50%)
                </p>
              </div>
            </div>
          </div>

          {/* Emergency Contact Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Emergency Contact</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emergencyContactName">Emergency Contact Name</Label>
                <Input
                  id="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                  placeholder="Full name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="emergencyContactPhone">Emergency Contact Number</Label>
                <Input
                  id="emergencyContactPhone"
                  type="tel"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
